import { Logo } from "@/components/Logo";
import { ContextButtonConnected } from "@/components/molecules/ContextButtonConnected";
import { PagePickerDialog } from "@/components/molecules/PagePickerDialog";
import { ContextSidebar } from "@/components/organisms/ContextSidebar";
import { SearchBar } from "@/components/organisms/SearchBar";
import { UserMenu } from "@/components/organisms/UserMenu";
import { WorkspaceSwitcher } from "@/components/molecules/WorkspaceSwitcher";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAnalysis } from "@/hooks/useAnalysis";
import { useDashboardUrlParams } from "@/hooks/useDashboardUrlParams";
import { useDomains } from "@/hooks/useDomains";
import { useSonnerToast } from "@/hooks/useSonnerToast";
import { useUser } from "@/hooks/useUser";
import { useUI } from "@/hooks/useUI";
import { useOrgRole } from "@/hooks/useOrgRole";
import { IdeaGenerationService } from "@/lib/ideaGenerationService";
import { isWordPressConnected } from "@/schemas";
import { useStore } from "@/store";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

type WpType = "posts" | "pages";

export const DashboardHeader = () => {
  const { hasPersonaSelected } = useUI();
  const { params, clearParams } = useDashboardUrlParams();
  const { currentUser } = useUser();
  const { currentDomain } = useDomains();
  const { canEdit } = useOrgRole();
  const { toast } = useSonnerToast();

  const wpIntegration = useStore((state) => state.wpIntegration);
  const wpIntegrationError = useStore(
    (state) => state.wpIntegrationError,
  );
  const wpActionLoading = useStore((state) => state.wpActionLoading);
  const createWpContent = useStore((state) => state.createWpContent);
  const orgId = useStore((state) => state.orgId);

  const pages = useStore((state) => state.pages);

  const [contextSidebarOpen, setContextSidebarOpen] = useState(false);
  const [ideaSheetOpen, setIdeaSheetOpen] = useState(false);
  const [pageSheetOpen, setPageSheetOpen] = useState(false);
  const [auditSheetOpen, setAuditSheetOpen] = useState(false);
  const [ideaInput, setIdeaInput] = useState("");
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [createType, setCreateType] = useState<WpType>("posts");
  const [createKeyword, setCreateKeyword] = useState("");
  const [createInstructions, setCreateInstructions] = useState("");

  const { startAnalysisJob, isAnalyzing, isGeneratingAnalysis } = useAnalysis();

  const hasProcessedUrlParam = useRef(false);

  useEffect(() => {
    if (params.sidebarOpen && !hasProcessedUrlParam.current) {
      hasProcessedUrlParam.current = true;
      queueMicrotask(() => setContextSidebarOpen(true));
      clearParams(["sidebarOpen"]);
    }
  }, [params.sidebarOpen, clearParams]);

  const auditEligiblePages = useMemo(
    () =>
      currentDomain?.id
        ? pages.filter((p) => p.domainId === currentDomain.id && !!p.url)
        : [],
    [pages, currentDomain?.id],
  );

  const isAuditDisabled =
    isAnalyzing ||
    isGeneratingAnalysis ||
    !hasPersonaSelected() ||
    !currentDomain?.id;

  const isPagesDisabled =
    !currentUser?.uid ||
    !currentDomain?.id ||
    !isWordPressConnected(wpIntegration) ||
    !!wpActionLoading.create;

  const handleAnalyzeClick = () => {
    if (isAuditDisabled) return;
    setAuditSheetOpen(true);
  };

  const handleGenerateIdeasClick = () => {
    if (isGeneratingIdeas || !currentUser?.uid || !currentDomain?.id) return;
    setIdeaSheetOpen(true);
  };

  const handleNewPageClick = () => {
    if (isPagesDisabled) return;
    setPageSheetOpen(true);
  };

  const handleIdeaSubmit = async () => {
    if (!ideaInput.trim()) {
      toast({
        title: "Enter an idea prompt",
        description: "Describe what you want to generate first.",
        variant: "destructive",
      });
      return;
    }

    if (!currentUser?.uid || !currentDomain?.id) {
      toast({
        title: "No domain selected",
        description: "Select a domain before generating ideas.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingIdeas(true);
    try {
      await IdeaGenerationService.generateIdeas({
        domain_id: currentDomain.id,
        user_input: ideaInput.trim(),
      });
      toast({
        title: "Idea generation started",
        description: "New ideas will appear in the table as they are generated.",
        variant: "success",
      });
      setIdeaInput("");
      setIdeaSheetOpen(false);
    } catch (error) {
      toast({
        title: "Failed to generate ideas",
        description:
          error instanceof Error ? error.message : "Unknown generation error",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!createKeyword.trim()) {
      toast({
        title: "Enter a keyword",
        description: "A keyword is required to create a page or post.",
        variant: "destructive",
      });
      return;
    }

    if (!orgId || !currentDomain?.id) {
      toast({
        title: "No domain selected",
        description: "Select a domain before creating WordPress content.",
        variant: "destructive",
      });
      return;
    }

    if (!isWordPressConnected(wpIntegration)) {
      // A domain can have an integration document that is present but broken --
      // core writes one from its sync error path. Telling someone who *did*
      // connect a site to "connect a site first" is both wrong and unactionable,
      // so when there is a real reason (`wpIntegrationError`), say it instead.
      toast({
        title: wpIntegrationError
          ? "WordPress connection needs attention"
          : "WordPress not connected",
        description:
          wpIntegrationError ?? "Connect a WordPress site for this domain first.",
        variant: "destructive",
      });
      return;
    }

    await createWpContent(
      orgId,
      currentDomain.id,
      createKeyword.trim(),
      createType,
      createInstructions.trim() || undefined,
    );

    const wpError = useStore.getState().wpActionError;
    if (!wpError) {
      toast({
        title: "Content generation started",
        description: `${
          createType === "pages" ? "Page" : "Post"
        } draft creation is in progress.`,
        variant: "success",
      });
      setCreateKeyword("");
      setCreateInstructions("");
      setCreateType("posts");
      setPageSheetOpen(false);
    }
  };

  // Every entry in this menu (analyze, generate ideas, create page) is an
  // editor-gated backend mutation, so viewers get no menu rather than a menu of
  // actions that 403.
  const actionButton = !canEdit ? null : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="h-10 bg-[#5A5DF6] text-white hover:bg-[#4A4DE6]">
          Actions
          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={isAuditDisabled}
          onSelect={handleAnalyzeClick}
        >
          {isAnalyzing || isGeneratingAnalysis ? "Analyzing..." : "Analyze"}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isGeneratingIdeas || !currentUser?.uid || !currentDomain?.id}
          onSelect={handleGenerateIdeasClick}
        >
          {isGeneratingIdeas ? "Generating..." : "Generate Ideas"}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isPagesDisabled}
          onSelect={handleNewPageClick}
        >
          {wpActionLoading.create ? "Creating..." : "New Page/Post"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const assistantButton = (
    <Button asChild variant="outline" className="h-10 shrink-0">
      <Link to="/assistant">Ask AI</Link>
    </Button>
  );

  return (
    <>
      <header className="border-b">
        <div className="container mx-auto px-4">
          <div className="block sm:hidden">
            <div className="flex flex-col gap-3 py-3">
              <div className="flex items-center gap-2">
                <WorkspaceSwitcher />
                <div className="flex-1 min-w-0">
                  <SearchBar />
                </div>
                <UserMenu />
              </div>

              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <ContextButtonConnected
                    onClick={() => setContextSidebarOpen(!contextSidebarOpen)}
                  />
                </div>
                {actionButton && (
                  <div className="flex-1 min-w-0">{actionButton}</div>
                )}
                <div className="shrink-0">{assistantButton}</div>
              </div>
            </div>
          </div>

          <div className="hidden sm:block">
            <div className="flex items-center gap-4 h-16">
              <a
                href="https://crolytics.ai/"
                rel="noopener noreferrer"
                className="cursor-default shrink-0"
              >
                <Logo />
              </a>

              <div className="flex items-center gap-4 flex-1 min-w-0 h-10">
                <WorkspaceSwitcher />
                <div className="flex-grow min-w-0">
                  <SearchBar />
                </div>
                <ContextSidebar
                  isOpen={contextSidebarOpen}
                  onOpenChange={setContextSidebarOpen}
                />
                {assistantButton}
                {actionButton && (
                  <div className="flex items-center shrink-0">
                    {actionButton}
                  </div>
                )}
              </div>

              <div className="flex items-center shrink-0">
                <UserMenu />
              </div>
            </div>
          </div>
        </div>
      </header>

      <Dialog open={ideaSheetOpen} onOpenChange={setIdeaSheetOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Generate Ideas</DialogTitle>
            <DialogDescription>
              Describe what you want to create for this domain. This sends the
              prompt to the ideas generation endpoint and streams results back
              into the ideas table.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Textarea
              placeholder="Describe the themes, keywords, audience, or business goals you want these ideas to target."
              value={ideaInput}
              onChange={(e) => setIdeaInput(e.target.value)}
              rows={8}
              maxLength={5000}
              disabled={isGeneratingIdeas}
            />
            <DialogFooter className="items-center justify-between sm:justify-between">
              <span className="text-xs text-muted-foreground">
                {ideaInput.length}/5000
              </span>
              <Button
                onClick={handleIdeaSubmit}
                disabled={isGeneratingIdeas || !currentUser?.uid || !currentDomain?.id}
              >
                {isGeneratingIdeas ? "Generating..." : "Generate Ideas"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pageSheetOpen} onOpenChange={setPageSheetOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>New Page or Post</DialogTitle>
            <DialogDescription>
              Create a WordPress draft and trigger content generation for the
              current domain.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={createType === "posts" ? "default" : "outline"}
                onClick={() => setCreateType("posts")}
              >
                Post
              </Button>
              <Button
                size="sm"
                variant={createType === "pages" ? "default" : "outline"}
                onClick={() => setCreateType("pages")}
              >
                Page
              </Button>
            </div>

            <Input
              placeholder="Keyword or page topic"
              value={createKeyword}
              onChange={(e) => setCreateKeyword(e.target.value)}
              disabled={!!wpActionLoading.create}
            />

            <Textarea
              placeholder="Custom instructions (optional)"
              value={createInstructions}
              onChange={(e) => setCreateInstructions(e.target.value)}
              rows={5}
              disabled={!!wpActionLoading.create}
            />

            <DialogFooter>
              <Button
                onClick={handleCreateSubmit}
                disabled={isPagesDisabled || !createKeyword.trim()}
              >
                {wpActionLoading.create ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <PagePickerDialog
        open={auditSheetOpen}
        onOpenChange={setAuditSheetOpen}
        title="Run Audit"
        description="Select a page to analyze. Only published pages with a URL can be audited."
        confirmLabel={
          isAnalyzing || isGeneratingAnalysis ? "Analyzing..." : "Run Audit"
        }
        pages={auditEligiblePages}
        onConfirm={(page) => {
          if (page.url) startAnalysisJob(page.url);
          setAuditSheetOpen(false);
        }}
        isConfirming={isAnalyzing || isGeneratingAnalysis}
      />
    </>
  );
};
